import React from 'react'

import {track, TrackingEvent} from '@helios/shared/utils/analytics'

/**
 * A custom hook which takes a ref to an element and a callback as required args, and invokes the provided callback
 * _once_ when the element enters the viewport for the first time. Defaults to a threshold of 0.5 but can be overridden.
 *
 * @param ref - A ref to the element to be watched
 * @param callback - The callback to be invoked when the element comes into view
 * @param options - IntersectionObserver init options. Has a default threshold of 0.5
 */
export const useEnteredViewport = (
    ref: React.MutableRefObject<any>,
    callback: (entry: IntersectionObserverEntry) => void,
    options?: IntersectionObserverInit
) => {
    const wrappedCallback = React.useCallback(callback, [callback])

    React.useEffect(() => {
        const node = ref.current

        if (!node) {
            return
        }

        const observerCallback: IntersectionObserverCallback = ([entry]) => {
            if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) {
                return
            }

            wrappedCallback(entry)

            // Stop watching after the first event for this element
            observer.unobserve(entry.target)
        }

        const observer = new IntersectionObserver(observerCallback, {
            threshold: 0.5,
            ...options,
        })

        observer.observe(node)

        return () => {
            observer.disconnect()
        }
    }, [ref, wrappedCallback, options])
}

type Action = 'card_carousel_interacted' | 'video_tabs_interacted' | 'testimonials_interacted'

export const useTrackFirstInteraction = () => {
    const [hasInteracted, setHasInteracted] = React.useState(false)

    return (action: Action) => {
        // If we've already tracked, do nothing
        if (hasInteracted) {
            return
        }

        // Track first interaction
        track(TrackingEvent.HomepageSectionActioned, {
            action,
        })

        setHasInteracted(true)
    }
}

type Section =
    | 'feature_blocks_section'
    | 'video_tabs_section'
    | 'card_carousel_section'
    | 'trust_points_section'
    | 'statistics_section'
    | 'testimonials_section'
    | 'logos_grid_section'
    | 'signup_banner_section'

export const useHomepageTracking = (ref: React.MutableRefObject<any>, section: Section) => {
    const trackFirstInteraction = useTrackFirstInteraction()

    useEnteredViewport(ref, () => {
        track(TrackingEvent.HomepageSectionActioned, {
            action: 'section_viewed',
            section,
        })
    })

    return {trackFirstInteraction}
}
